import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@reactuses/core";
import { Container as Grid } from "react-bootstrap";
import { formatNumber } from "../../../utils/formatNumber";
import { Accordion } from "../../../components/core/basic/Accordion";
import { DownloadV4Report } from "../../../modules/perf_reports/DownloadReport";
import { DatesProvider } from "../../../dates/DatesContext";
import { useDates } from "../../../dates/DatesContext";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import moment from "moment";
import BreadcrumbDates from "../../../components/core/blocks/BreadcrumbDates";
import getMetricSpec from "../../../modules/merchandising/utils/metrics";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import EmptyState from "../../../components/custom/merchandise/EmptyState";
import MetricsCard from "../../../components/custom/merchandise/MetricsCard";
import AppStatsCard from "../../../components/custom/merchandise/AppStatsCard";
import { DevErrorDisplay } from "../../../components/core/blocks/DevErrorDisplay";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../../../utils/dashboardLinks";
import { RT8FilterWidget } from "../../../modules/merchandising/components/tables/FilterWidget";
import VerifyEmailModal from "../../../modules/onboarding/components/VerifyEmailModal";
import { useIsSPAPIConnected } from "../../../hooks/useIsSPAPIConnected";
import { AnnotationsTable } from "../../../components/custom/growth/AnnotationsTable";
import SalesMapUSA from "../../../components/custom/maps/usa";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import { useStandardEntityMetrics } from "../../../modules/merchandising/components/tables/metricUtils";
import { useAppSelector } from "../../../redux/hooks";
import { ProductCell } from "../../../modules/merchandising/components/cells/ProductCell";
import { getDashboardTo, DASHBOARD_TYPES } from "../../../modules/merchandising/components/cells/ProductCell";
import ExtensionInfo from "../../../modules/onboarding/components/ExtensionInfo";
import { css } from "../../../../styled-system/css";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import { Button } from "../../../components/core/basic/Button";
import { ExpandedContent } from "../../../components/custom/merchandise/ExpandedContent";
import { MerchCategoryCell } from "../../../components/custom/merchandise/cells/MerchCategoryCell";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import { useGetAnnotations } from "../../../modules/annotations/api/useGetAnnotations";
import { useGetV4CategoryReportByCategory } from "../../../modules/perf_reports/api/category/v4CategoryReportByCategory";
import { useGetV4CategoryReportByDate } from "../../../modules/perf_reports/api/category/v4CategoryReportByDate";
import { useGetV4CategoryReportTotals } from "../../../modules/perf_reports/api/category/v4CategoryReportTotals";
import { useV4CategoryReportExport } from "../../../modules/perf_reports/api/category/v4CategoryReportExport";
import { useGetV4ProductReportByProduct } from "../../../modules/perf_reports/api/product/v4ProductReportByProduct";
import { useV4ProductReportExport } from "../../../modules/perf_reports/api/product/v4ProductReportExport";
import { useClientTable, useServerTable } from "../../../components/table/tableFeatures";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { RT8Pagination } from "../../../components/table/Pagination";
import { SearchInput } from "../../../components/fields/base/TextField";
import { Table as DataTable } from "../../../components/table/Table";
import { idsToVisibilityState, resetTable } from "../../../components/table/utils";
import { customFilterFn } from "../../../components/table/filterFns";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { MdRotateLeft } from "react-icons/md";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { DataViewPicker } from "../../../modules/dataViews/components/DataViewPicker";

const columnHelper = createColumnBuilder();
const EMPTY_ARRAY = [];

const MerchTableToolbar = ({ table, viewKey, datasetKey }) => {
  const { t } = useTranslation(["merchandising"]);
  return (
    <HStack padding={"small"} fontSize={"large"} width={"100%"} justifyContent={"space-between"} gap={"large"}>
      <VStack flex="1" alignItems={"start"}>
        <HStack width={"100%"}>
          <Button variant={"contentOnly"} onPress={() => resetTable(table, viewKey)} fontSize={"xxlarge"}>
            <MdRotateLeft />
          </Button>
          <RT8ColumnSelector table={table} />
          {viewKey && <DataViewPicker viewKey={viewKey} datasetKey={datasetKey} table={table} />}
          <SearchInput
            aria-label={t(($) => $.merchandising["Search"])}
            isExpandable
            css={{ flex: "0 1 350px" }}
            value={table.state.globalFilter ?? ""}
            onChange={table.setGlobalFilter}
          />
        </HStack>
        <RT8FilterWidget table={table} />
      </VStack>
      <RT8Pagination className={css({ marginLeft: "auto", paddingLeft: "large" })} table={table} />
    </HStack>
  );
};

const CategoryTable = ({ state, initialState, setters, debouncedSearch }) => {
  const { t } = useTranslation(["merchandising"]);
  const standardMetricIds = useStandardEntityMetrics("category");
  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row?.category_name ?? row?.name ?? "", {
        id: "category_name",
        header: t(($) => $.merchandising["Category"]),
        size: 400,
        minSize: 400,
        enableHiding: false,
        enableColumnFilter: false,
        cell: (props) => (
          <MerchCategoryCell
            value={props.getValue()}
            original={{
              category_id: props.row.original?.category_id?.toString?.() ?? props.row.original?.category_id,
            }}
          />
        ),
        meta: {
          justify: "start",
          columnSelectorOptions: {
            label: t(($) => $.merchandising["Category"]),
          },
        },
      }),
      ...standardMetricIds.map((metricId) => columnHelper.metric(metricId)),
    ],
    [standardMetricIds, t],
  );

  const categoryByCategoryOptionsGetter = useGetV4CategoryReportByCategory();
  const categoryByCategoryReportQuery = useBlockWindowQuery(categoryByCategoryOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debouncedSearch,
  });

  const table = useServerTable({
    data: categoryByCategoryReportQuery.data?.data,
    columns,
    enableGlobalFilter: true,
    rowCount: categoryByCategoryReportQuery.data?.count || 0,
    getRowId: (row) => row.category_id?.toString?.() ?? row.category_id,
    initialState: initialState,
    state: state,
    ...setters,
    alternateSortingEnabled: true,
    meta: {
      showSkeleton:
        categoryByCategoryReportQuery.isFetching &&
        (!categoryByCategoryReportQuery.data || categoryByCategoryReportQuery.isPlaceholderData),
      columnSelectOptions: {
        modalTitle: t(($) => $.merchandising["Select Category Columns"]),
      },
    },
  });

  if (categoryByCategoryReportQuery.isError) {
    return (
      <DevErrorDisplay
        error={categoryByCategoryReportQuery.error}
        userMessage={t(($) => $.merchandising["Error loading category report."])}
      />
    );
  }

  return (
    <VStack gap={"small"} alignItems={"stretch"} width={"100%"}>
      <MerchTableToolbar table={table} viewKey="merchCategoriesTable" datasetKey="category" />
      <DataTable table={table} className={css({ minWidth: "100%" })} />
    </VStack>
  );
};

const TopProductsTable = ({ defaultVisibleMetricIds }) => {
  const { t } = useTranslation(["merchandising"]);
  const user = useAppSelector((state) => state.user);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  const standardMetricIds = useStandardEntityMetrics("product");
  const columns = useMemo(
    () => [
      columnHelper.accessor("product_title", {
        id: "product_title",
        header: t(($) => $.merchandising["Product"]),
        size: 400,
        minSize: 200,
        enableHiding: false,
        enableColumnFilter: false,
        cell: (props) => {
          const row = props.row.original ?? {};
          return (
            <ProductCell
              original={{
                ...row,
                product_id: row.product_id?.toString?.() ?? row.product_id,
                pricing_plan__status: row.pricing_plan_status ?? row.pricing_plan__status,
                price_override_end: row.price_override_end ? row.price_override_end.slice(0, 10) : "",
                cellContext: "merchandising",
              }}
              value={props.getValue() ?? row.product_title}
              showInventoryBadge={false}
              className={css({ marginRight: "auto", overflowX: "hidden" })}
            />
          );
        },
        meta: {
          justify: "start",
          columnSelectorOptions: {
            label: t(($) => $.merchandising["Product"]),
          },
        },
      }),
      columnHelper.accessor("product_quantity_fba", {
        id: "product_quantity_fba",
        header: t(($) => $.merchandising["Current Inventory FBA"]),
        size: 160,
        minSize: 110,
        cell: ({ getValue }) => formatNumber(getValue()),
        meta: {
          columnFilterOptions: {
            format: "number",
          },
          columnSelectorOptions: {
            label: t(($) => $.merchandising["Current Inventory FBA"]),
          },
          justify: "end",
        },
      }),
      columnHelper.accessor("product_quantity", {
        id: "product_quantity",
        header: t(($) => $.merchandising["Current Inventory MFA"]),
        size: 160,
        minSize: 110,
        cell: ({ getValue }) => formatNumber(getValue()),
        meta: {
          columnFilterOptions: {
            format: "number",
          },
          columnSelectorOptions: {
            label: t(($) => $.merchandising["Current Inventory MFA"]),
          },
          justify: "end",
        },
      }),
      ...standardMetricIds.map((metricId) => columnHelper.metric(metricId)),
    ],
    [standardMetricIds, t],
  );
  const { defaultVisibleColumns, columnOrder, pinnedColumns } = useMemo(() => {
    const orderedVisibleMetrics = defaultVisibleMetricIds.filter((id) => standardMetricIds.includes(id));
    const visibleMetrics = orderedVisibleMetrics.length ? orderedVisibleMetrics : standardMetricIds;
    return {
      defaultVisibleColumns: ["product_title", ...visibleMetrics],
      columnOrder: ["product_title", ...visibleMetrics, "last_bsr_rank", "product_quantity_fba", "product_quantity"],
      pinnedColumns: ["product_title"],
    };
  }, [defaultVisibleMetricIds, standardMetricIds]);

  const columnVisibility = useMemo(
    () =>
      idsToVisibilityState(
        defaultVisibleColumns,
        columns.map((column) => column.id),
      ),
    [columns, defaultVisibleColumns],
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "merchTopProductsTable-v2",
    initialState: {
      columnVisibility,
      columnOrder,
      columnPinning: {
        start:
          pinnedColumns.length ? pinnedColumns
          : columnOrder.length ? [columnOrder[0]]
          : [],
        end: [],
      },
      sorting: [{ id: "sales", desc: true }],
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  });

  const productByProductOptionsGetter = useGetV4ProductReportByProduct();
  const productReportQuery = useQuery({
    ...productByProductOptionsGetter({
      pagination: { pageIndex: 0, pageSize: 500 },
      sorting: [{ id: "sales", desc: true }],
      filters: [],
      search: "",
    }),
    placeholderData: keepPreviousData,
  });

  const exportMutation = useV4ProductReportExport(state.columnFilters);

  const table = useClientTable({
    data: productReportQuery.data?.data,
    columns,
    defaultColumn: {
      filterFn: customFilterFn,
    },
    alternateSortingEnabled: true,
    getRowCanExpand: () => true,
    getRowId: (row) => row.product_id?.toString?.() ?? row.product_id,
    enableGlobalFilter: true,
    globalFilterFn: (row, _columnId, filterValue) => {
      if (!filterValue) return true;
      const search = filterValue.toLowerCase();
      const { product_title, asin, sku } = row.original ?? {};
      return [product_title, asin, sku].some((field) => field?.toLowerCase?.().includes(search));
    },
    initialState: initialState,
    state: state,
    ...setters,
    meta: {
      showSkeleton: productReportQuery.isFetching && (!productReportQuery.data || productReportQuery.isPlaceholderData),
      columnSelectOptions: {
        modalTitle: t(($) => $.merchandising["Select Product Columns"]),
      },
    },
  });

  if (productReportQuery.isError) {
    return (
      <DevErrorDisplay
        error={productReportQuery.error}
        userMessage={t(($) => $.merchandising["Error loading product report."])}
      />
    );
  }

  return (
    <VStack gap={"small"} alignItems={"stretch"} width={"100%"}>
      <MerchTableToolbar table={table} viewKey="merchTopProductsTable-v2" datasetKey="product" />
      <DataTable
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => <ExpandedContent marketPlace={marketPlace} row={row} user={user} channel={channel} />}
      />
      <HStack justifyContent={"end"} width={"100%"}>
        <DownloadV4Report downloadMutation={exportMutation} />
      </HStack>
    </VStack>
  );
};

const Dashboard = () => {
  const { t } = useTranslation(["merchandising"]);
  const user = useAppSelector((state) => state.user);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  const { group, start, end } = useDates();
  const longDate = useDateFormatter("long");
  const acosOrRoas = useAcosOrRoas();
  const [annotationsTab, setAnnotationsTab] = useState("advertisement");
  const hasTop500Module = useModuleEnabled("TOP500");

  const { overviewMetrics, topNProductsMetrics, showTopProducts } = useMemo(() => {
    const defaultCategoryMetricIds =
      channel === "shopify" ?
        ["sales", "units", "margin"]
      : [
          "sales",
          "units",
          "total_acos",
          "ad_spend",
          "ad_sales",
          "acos",
          "conversion_rate",
          "cpc",
          "ctr",
          "clicks",
          "impressions",
        ];

    const defaultProductMetricIds = [
      "sales",
      "units",
      "margin",
      "cost",
      "roms",
      "page_views",
      "conversion_rate",
      "ad_spend",
      "ad_sales",
      "acos",
      "ctr",
      "clicks",
      "impressions",
      "orders",
      "total_acos",
    ];

    if (channel === "shopify") {
      return {
        showTopProducts: false,
        topNProductsMetrics: { defaultVisibleMetricIds: defaultProductMetricIds },
        overviewMetrics: {
          metrics: [getMetricSpec("sales"), getMetricSpec("units"), getMetricSpec("margin")],
          defaultVisibleMetricIds: defaultCategoryMetricIds,
        },
      };
    }

    const roasMetrics = [getMetricSpec("troas"), getMetricSpec("roas")];
    const acosMetrics = [getMetricSpec("total_acos"), getMetricSpec("acos")];

    return {
      showTopProducts: true,
      topNProductsMetrics: {
        defaultVisibleMetricIds: defaultProductMetricIds,
      },
      overviewMetrics: {
        metrics: [
          getMetricSpec("sales"),
          getMetricSpec("units"),
          getMetricSpec("margin"),
          ...acosOrRoas(acosMetrics, roasMetrics),
          getMetricSpec("ad_sales"),
          getMetricSpec("ad_spend"),
          ...acosOrRoas(roasMetrics, acosMetrics),
          getMetricSpec("roms"),
          getMetricSpec("cost"),
          getMetricSpec("promo_cost"),
          getMetricSpec("ctr"),
          getMetricSpec("orders"),
          getMetricSpec("cpc"),
          getMetricSpec("conversion_rate"),
        ],
        defaultVisibleMetricIds: defaultCategoryMetricIds,
      },
    };
  }, [channel, acosOrRoas]);

  // Category overview data fetching
  const standardMetricIds = useStandardEntityMetrics("category");

  const { defaultVisibleColumns, columnOrder, pinnedColumns } = useMemo(() => {
    const orderedVisibleMetrics = overviewMetrics.defaultVisibleMetricIds.filter((id) =>
      standardMetricIds.includes(id),
    );
    const visibleMetrics = orderedVisibleMetrics.length ? orderedVisibleMetrics : standardMetricIds;
    const resolvedVisibleColumns = ["category_name", ...visibleMetrics];
    return {
      defaultVisibleColumns: resolvedVisibleColumns,
      columnOrder: resolvedVisibleColumns,
      pinnedColumns: ["category_name"],
    };
  }, [overviewMetrics.defaultVisibleMetricIds, standardMetricIds]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "merchCategoriesTable",
    initialState: {
      columnVisibility: idsToVisibilityState(defaultVisibleColumns, ["category_name", ...standardMetricIds]),
      columnOrder,
      columnPinning: {
        start: pinnedColumns,
        end: [],
      },
      sorting: [{ id: "sales", desc: true }],
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  });

  const debouncedSearch = useDebounce(state.globalFilter, 500);

  const exportMutation = useV4CategoryReportExport(state.columnFilters);

  const categoryByDateOptionsGetter = useGetV4CategoryReportByDate();
  const categoryByDateReportQuery = useQuery({
    ...categoryByDateOptionsGetter({
      filters: state.columnFilters,
      search: debouncedSearch,
    }),
    placeholderData: keepPreviousData,
  });

  const categoryTotalsOptionsGetter = useGetV4CategoryReportTotals();
  const categoryTotalsReportQuery = useQuery({
    ...categoryTotalsOptionsGetter({
      filters: state.columnFilters,
      search: debouncedSearch,
    }),
    placeholderData: keepPreviousData,
  });

  const getAnnotationsOptions = useGetAnnotations();
  const annotationQuery = useQuery({
    ...getAnnotationsOptions({ category: null }),
    placeholderData: keepPreviousData,
  });

  const annotationCounts = useMemo(() => {
    const data = annotationQuery.data?.flatMap((date) => date.annotations) ?? EMPTY_ARRAY;

    return data?.reduce(
      (acc, annotation) => {
        if (annotation.product) {
          acc.productCount++;
        }
        if (annotation.advertisement) {
          acc.advertisementCount++;
        }
        return acc;
      },
      { productCount: 0, advertisementCount: 0 },
    );
  }, [annotationQuery.data]);

  const breadCrumbItems = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.merchandising["Merchandising Dashboard"]),
      href: getDashboardTo({
        resourceId: null,
        type: DASHBOARD_TYPES.ROOT,
      }),
      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      unsorted: true,
      dropdownSubtitle: t(($) => $.merchandising["Dashboards"]),
    },
  ];

  const isSPAPIConnected = useIsSPAPIConnected();

  const showEmpty = useMemo(() => {
    const totalSalesStart = user.total_sales_start_date?.[marketPlace.marketPlace];
    if (totalSalesStart) {
      if (moment(totalSalesStart).isBefore(moment().subtract(2, "days"))) {
        return false;
      }
      return "COLLECTING_DATA";
    }
    if (isSPAPIConnected) {
      return "COLLECTING_DATA";
    }
    return "NO_DATA";
  }, [user, marketPlace.marketPlace, isSPAPIConnected]);

  if (categoryTotalsReportQuery.isError) {
    return (
      <DevErrorDisplay
        error={categoryTotalsReportQuery.error}
        userMessage={t(($) => $.merchandising["Error loading dashboard."])}
      />
    );
  }

  if (categoryByDateReportQuery.isError) {
    return (
      <DevErrorDisplay
        error={categoryByDateReportQuery.error}
        userMessage={t(($) => $.merchandising["Error loading dashboard."])}
      />
    );
  }

  if (user.organization_type === "vendor" && channel === "walmart") {
    return <Navigate to="/user/advertising/dashboard" replace />;
  }

  return (
    <Grid
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      {showEmpty ?
        <EmptyState type={showEmpty} />
      : <VStack gap={"large"} alignItems={"stretch"} width={"100%"} paddingBottom={"large"}>
          <BreadcrumbDates items={breadCrumbItems} />

          <VStack flex="1" minWidth="0" alignItems={"stretch"}>
            <MetricsCard
              title={t(($) => $.merchandising["Overview"])}
              byline={
                <>
                  {longDate.format(start.toDate())}
                  {" - "}
                  {longDate.format(end.toDate())}
                </>
              }
              metrics={overviewMetrics.metrics}
              totals={{
                data: categoryTotalsReportQuery.data?.data,
                loading:
                  categoryTotalsReportQuery.isFetching &&
                  (!categoryTotalsReportQuery.data || categoryTotalsReportQuery.isPlaceholderData),
              }}
              timeline={{
                data: categoryByDateReportQuery.data?.data ?? EMPTY_ARRAY,
                loading:
                  categoryByDateReportQuery.isFetching &&
                  (!categoryByDateReportQuery.data || categoryByDateReportQuery.isPlaceholderData),
              }}
              initialSelectedMetric={
                channel !== "shopify" ?
                  ["sales", "units", acosOrRoas("tacos", "troas"), "margin"]
                : ["sales", "units", "margin"]
              }
              showAnnotations={true}
              group={group}
              showMetricsSelector={channel !== "shopify"}
              id={"merchCategoriesTable"}
              context={"organization"}
              extendMetrics={channel !== "shopify"}
            >
              <VStack gap={"large"} alignItems={"stretch"}>
                <Accordion title={t(($) => $.merchandising["Categories"])} defaultExpanded>
                  <CategoryTable
                    state={state}
                    initialState={initialState}
                    setters={setters}
                    debouncedSearch={debouncedSearch}
                  />
                  <HStack justifyContent={"end"} width={"100%"} paddingTop={"large"}>
                    <DownloadV4Report downloadMutation={exportMutation} />
                  </HStack>
                </Accordion>
                {hasTop500Module && showTopProducts && (
                  <Accordion title={t(($) => $.merchandising["Top 500 Products"])}>
                    <TopProductsTable defaultVisibleMetricIds={topNProductsMetrics.defaultVisibleMetricIds} />
                  </Accordion>
                )}

                {channel !== "shopify" && (
                  <Accordion title={t(($) => $.merchandising["Impact Analysis"])}>
                    <HStack justifyContent={"end"} width={"100%"} className={css({ paddingRight: "large" })}>
                      <ButtonGroup
                        onSelect={(value) => {
                          setAnnotationsTab(value);
                        }}
                        value={annotationsTab}
                        buttons={[
                          {
                            value: "advertisement",
                            label: t(($) => $.merchandising["Media Plans"]),
                            counter: annotationCounts["advertisementCount"],
                          },
                          {
                            value: "product",
                            label: t(($) => $.merchandising["Products"]),
                            counter: annotationCounts["productCount"],
                          },
                        ]}
                        colorPalette={"buttonGroup.fuchsia"}
                      />
                    </HStack>
                    <AnnotationsTable
                      data={annotationQuery.data}
                      isLoading={annotationQuery.isLoading}
                      annotationType={annotationsTab}
                    />
                  </Accordion>
                )}
                {marketPlace.marketPlace === "USA" && channel === "amazon" && (
                  <Accordion title={t(($) => $.merchandising["Sales By State"])}>
                    <SalesMapUSA
                      start_date={start.format("YYYY-MM-DD")}
                      end_date={end.format("YYYY-MM-DD")}
                      marketPlace={marketPlace}
                      categoryId={null}
                    />
                  </Accordion>
                )}
              </VStack>
            </MetricsCard>

            <AppStatsCard />
          </VStack>
        </VStack>
      }
    </Grid>
  );
};

const MerchDashboardOverview = () => {
  return (
    <DatesProvider>
      <VerifyEmailModal />
      <ExtensionInfo />
      <Dashboard />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/dashboard")({
  component: MerchDashboardOverview,
  staticData: { application: "Merchandising" },
});
